import { useMemo, useState } from 'react';
import { CAMPO_OPCIONES, PARENTESCO_OPCIONES } from '@lib/catalogs';
import {
  agregarAdherenteAlLegajo,
  type Adherente,
  type AdicionalBis,
  type DatosIncompletosError,
} from '@lib/services/adherentes.service';
import { validarFilaAsociado } from '@lib/validar-asociado';
import { CampoControl } from './EditAsociadoModal';
import { FIELDS } from './asociado-campos';

/**
 * Modal de resolución de un legajo bis: muestra quién va a dónde (cada
 * adicional → el legajo del titular) y obliga a completar los datos que el
 * validador del padrón marca con error ANTES de agregar. El backend valida
 * de nuevo (422 con detalle por integrante si algo sigue inválido), mueve a
 * cada adicional del padrón en que llegó suelto al padrón del titular y
 * recién ahí lo suma al legajo.
 */

/** Campos del editor que el adicional NO debería cambiar acá: el legajo lo
 *  define el destino, el parentesco 0 está vedado (ya hay titular) y el 13
 *  (adherente) no se da de alta en esta etapa (02/10/2026). */
const PARENTESCO_OPCIONES_ADICIONAL = PARENTESCO_OPCIONES.filter(
  (o) => o.value !== '0' && o.value !== '13',
);

/**
 * Errores del form de un adicional (vacío + formato), con las MISMAS reglas que
 * el backend. Ignora `legajo` (lo define el destino, no se edita acá) y aplica
 * el veto de parentesco 0 (el legajo ya tiene titular). Vacío = fila válida.
 */
function erroresDeForm(form: Record<string, string>): Record<string, string> {
  const errs = validarFilaAsociado(form);
  delete errs.legajo;
  if ((form.parentesco ?? '').trim() === '0') {
    errs.parentesco = 'El adicional no puede ser titular (0): elegí cónyuge o hijo';
  }
  return errs;
}

interface Props {
  adherente: Adherente;
  onClose: () => void;
  onSuccess: (integrantesAgregados: number) => void;
}

type FormPorAdicional = Record<string, Record<string, string>>;

function initForms(adicionales: AdicionalBis[]): FormPorAdicional {
  const forms: FormPorAdicional = {};
  for (const a of adicionales) {
    const f: Record<string, string> = {};
    FIELDS.forEach(({ key }) => {
      f[key as string] = ((a as unknown as Record<string, unknown>)[key as string] ?? '')
        .toString();
    });
    forms[a.asociadoId] = f;
  }
  return forms;
}

export function AgregarAdherenteModal({ adherente, onClose, onSuccess }: Props) {
  const [forms, setForms] = useState<FormPorAdicional>(() => initForms(adherente.adicionales));
  const [erroresBackend, setErroresBackend] = useState<Record<string, Record<string, string>>>({});
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Validación intensa EN VIVO: re-valida cada adicional con las reglas del
  // padrón (vacío + formato). Marca/bloquea no solo los vacíos sino también los
  // llenos-inválidos (ej. parentesco 'FAM'), que antes pasaban el chequeo.
  const erroresCliente = useMemo(() => {
    const out: Record<string, Record<string, string>> = {};
    for (const a of adherente.adicionales) {
      out[a.asociadoId] = erroresDeForm(forms[a.asociadoId] ?? {});
    }
    return out;
  }, [forms, adherente.adicionales]);
  const hayErrores = Object.values(erroresCliente).some((e) => Object.keys(e).length > 0);

  const update = (asociadoId: string, campo: string, valor: string) => {
    setForms((p) => ({ ...p, [asociadoId]: { ...p[asociadoId], [campo]: valor } }));
    setErroresBackend((p) => {
      if (!p[asociadoId]?.[campo]) return p;
      const next = { ...p, [asociadoId]: { ...p[asociadoId] } };
      delete next[asociadoId][campo];
      return next;
    });
  };

  const handleSubmit = async () => {
    // Pre-chequeo local INTENSO: no se agrega si algún adicional tiene campos
    // inválidos (vacíos o con formato malo). El backend revalida igual.
    if (hayErrores) {
      setErrorGeneral('Completá y corregí los campos marcados en rojo antes de agregar.');
      return;
    }

    setSaving(true);
    setErrorGeneral(null);
    try {
      const integrantes = adherente.adicionales.map((a) => ({
        asociadoId: a.asociadoId,
        // Mandamos todos los campos no vacíos del form: el backend sanitiza
        // al set editable y mergea sobre el asociado actual.
        campos: Object.fromEntries(
          Object.entries(forms[a.asociadoId] ?? {}).filter(([, v]) => v.trim() !== ''),
        ),
      }));
      const res = await agregarAdherenteAlLegajo(adherente.id, integrantes);
      onSuccess(res.integrantesAgregados);
      onClose();
    } catch (err) {
      const data = (err as { response?: { data?: unknown } })?.response?.data as
        | (Partial<DatosIncompletosError> & { message?: string })
        | undefined;
      if (data?.code === 'DATOS_INCOMPLETOS' && Array.isArray(data.integrantes)) {
        const porAsociado: Record<string, Record<string, string>> = {};
        for (const i of data.integrantes) {
          porAsociado[i.asociadoId] = Object.fromEntries(
            i.errores.map((e) => [e.field, e.message]),
          );
        }
        setErroresBackend(porAsociado);
        setErrorGeneral(data.message ?? 'Hay integrantes con datos inválidos.');
      } else {
        setErrorGeneral(
          (data?.message as string) ?? 'No se pudo agregar al legajo. Probá de nuevo.',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modalBackdrop" onClick={onClose}>
      <div className="modalCard" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <header className="modalHeader">
          <div>
            <h2 className="heading-5">Agregar al legajo {adherente.numero}</h2>
            <p className="text-element-mid">
              {adherente.adicionales.length === 1 ? 'El adicional pasa' : 'Los adicionales pasan'}{' '}
              al grupo de{' '}
              <strong>
                {adherente.titularApellido} {adherente.titularNombre}
              </strong>{' '}
              (DNI {adherente.titularDocumento}) y a su padrón. Revisá y completá los datos:
              las validaciones se re-ejecutan al guardar.
            </p>
          </div>
          <button className="modalClose" onClick={onClose}>
            ×
          </button>
        </header>

        {adherente.adicionales.map((a) => (
          <AdicionalForm
            key={a.asociadoId}
            adicional={a}
            form={forms[a.asociadoId] ?? {}}
            errores={erroresBackend[a.asociadoId] ?? {}}
            erroresCliente={erroresCliente[a.asociadoId] ?? {}}
            onChange={(campo, valor) => update(a.asociadoId, campo, valor)}
          />
        ))}

        {errorGeneral && (
          <p className="status status-error" style={{ display: 'block', marginTop: 12 }}>
            {errorGeneral}
          </p>
        )}

        <footer className="modalFooter">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving || hayErrores}
            title={hayErrores ? 'Completá y corregí los campos marcados antes de agregar' : undefined}
          >
            <span className="btn-font-style">
              {saving ? 'Agregando…' : 'Agregar al legajo'}
            </span>
          </button>
        </footer>
      </div>
    </div>
  );
}

function AdicionalForm({
  adicional,
  form,
  errores,
  erroresCliente,
  onChange,
}: {
  adicional: AdicionalBis;
  form: Record<string, string>;
  errores: Record<string, string>;
  erroresCliente: Record<string, string>;
  onChange: (campo: string, valor: string) => void;
}) {
  // Mensaje por campo: el del backend (si volvió un 422) tiene prioridad; si no,
  // el de la validación cliente en vivo. Un campo está en rojo si tiene alguno.
  const errorDe = (k: string) => errores[k] ?? erroresCliente[k];
  const invalidos = Object.keys(erroresCliente).length;
  return (
    <section style={{ marginTop: 16 }}>
      <div className="rowBetween" style={{ alignItems: 'baseline' }}>
        <h3 className="heading-6" style={{ margin: 0 }}>
          {adicional.apellSolt || adicional.nombres
            ? `${adicional.apellSolt} ${adicional.nombres}`.trim()
            : adicional.asociadoId}
          {adicional.documento ? ` — DNI ${adicional.documento}` : ''}
        </h3>
        {invalidos > 0 ? (
          <span className="status status-warning" style={{ fontSize: 12 }}>
            {invalidos} dato{invalidos === 1 ? '' : 's'} a corregir
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--color-success, #15803d)', fontWeight: 600 }}>
            ✓ Datos completos
          </span>
        )}
      </div>

      <div className="modalGrid" style={{ marginTop: 8 }}>
        {FIELDS.map((f) => {
          const k = f.key as string;
          // El legajo del adicional lo define el destino: solo lectura.
          if (k === 'legajo') return null;
          const msg = errorDe(k);
          // `*` para los campos que originalmente venían con error o que hoy
          // siguen inválidos (el operador tiene que tocarlos).
          const requerido = adicional.camposConError.includes(k) || Boolean(erroresCliente[k]);
          const opciones =
            k === 'parentesco' ? PARENTESCO_OPCIONES_ADICIONAL : CAMPO_OPCIONES[k];
          return (
            <label key={k} className="formField">
              <span>
                {f.label}
                {requerido ? ' *' : ''}
              </span>
              <CampoControl
                fieldKey={k}
                value={form[k] ?? ''}
                placeholder={f.placeholder}
                hasError={Boolean(msg)}
                opciones={opciones}
                onChange={(v) => onChange(k, v)}
              />
              {msg && (
                <span
                  className="status status-error"
                  style={{ display: 'block', fontSize: 12, marginTop: 4 }}
                >
                  {msg}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </section>
  );
}
